import React from 'react';
import styled, { css } from 'styled-components';
import { useI18n } from '@whizz/react-i18n';
import type { ThemeProps } from 'styled-components';
import type { MaterialIconType, StudentStatusColorType, ThemeType } from '../../../util/types';
import type { StudentStatusGroupType } from '../../types';
import { getPolishedColors } from '../../../util/styled';
import { MaterialIcon } from '../../../components';
import { GLOBAL_ICONS } from '../../../util/vars';

export type GreenTickStatuses = 'green' | 'amber' | 'red';

const greenTickStatuses: Record<GreenTickStatuses, {
  icon: MaterialIconType,
  color: 'positive' | 'warning' | 'negative',
}> = {
  green: {
    icon: GLOBAL_ICONS.tick,
    color: 'positive',
  },
  amber: {
    icon: GLOBAL_ICONS.help,
    color: 'warning',
  },
  red: {
    icon: GLOBAL_ICONS.cross,
    color: 'negative',
  },
};

const getColors = (props: ThemeProps<ThemeType> & { $color: StudentStatusColorType }) => {
  const colors = getPolishedColors(props);
  return css`
    background: ${colors.main.color};
    color: ${colors.main.readable};
  `;
};

const StudentStatusStyled = styled('div')<{
  $color: StudentStatusColorType
}>`
  ${(props) => getColors(props)}
`;

const StudentStatus = ({ status }: { status: StudentStatusGroupType }): JSX.Element => {
  const { translate } = useI18n();
  return (
    <div className={'bg-white rounded'}>
      <StudentStatusStyled
        $color={status}
        className={'rounded py-3 px-6 w-full text-center'}
        title={status}>
        {translate(`pages.REPORTS.STUDENT_STATUS.${status}`)}
      </StudentStatusStyled>
    </div>
  );
};

const GreenTickStatus = ({ status }: { status: GreenTickStatuses }): JSX.Element => {
  const { icon, color } = greenTickStatuses[status];
  return (
    <div className={'flex justify-center'}>
      <MaterialIcon
        icon={icon}
        color={color}
        title={status}
      />
    </div>
  );
};

const TableHeader = ({ title, subtitle = '' }: { title: string, subtitle?: string }): JSX.Element => (
  <div className={'flex flex-col items-center justify-between text-center h-full'}>
    <div className={'font-bold whitespace-normal'}>{title}</div>
    <div>{subtitle}</div>
  </div>
);

export { GreenTickStatus, StudentStatus, TableHeader };
